import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { PBPUTILS } from "../../config/API";
import jsPDF from 'jspdf';
import SuccessPage from '../SuccessPage';

const MotorFuelDistributorReport = () => {
  // Available retailers for dropdown - initially empty, will be populated from API
  const [availableRetailers, setAvailableRetailers] = useState([]);
  const [retailerData, setRetailerData] = useState([]); // Store full retailer data including sval

  // Get taxpayer ID and business name from localStorage
  const [taxpayerId, setTaxpayerId] = useState('');
  const [businessName, setBusinessName] = useState('');

  // Month options for dropdown
  const monthOptions = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Year options (current year and previous years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => (currentYear - 3 + i).toString());

  // Function to get default month and year based on current date
  const getDefaultReportDate = () => {
    const now = new Date();
    const currentMonth = now.getMonth(); // 0 = January, 11 = December
    const currentYear = now.getFullYear();

    // If current month is January (0), then select December of previous year
    if (currentMonth === 0) {
      return {
        month: 'December',
        year: (currentYear - 1).toString()
      };
    }

    // Otherwise, select previous month of current year
    return {
      month: monthOptions[currentMonth - 1],
      year: currentYear.toString()
    };
  };

  const [formData, setFormData] = useState({
    // Report type
    reportType: 'original',
    amendedMonth: '',
    amendedYear: '',

    // Distributor information - will be set from localStorage
    distributorName: '',
    distributorAddress: '',

    // Retailer data - start with empty array, will add dynamically
    retailers: [],

    // Selected retailer for dropdown
    selectedRetailer: '',
    selectedRetailerId: '',

    // Payment information
    checkDate: '',
    numberOfInvoices: '',

    // Certification
    noSales: false
  });

  // Report Information State - initialize with default values
  const [reportInfo, setReportInfo] = useState({
    month: '',
    year: ''
  });

  // Printed Name and Title State
  const [printedName, setPrintedName] = useState('');
  const [title, setTitle] = useState('');

  // File upload state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadStatus, setUploadStatus] = useState('');
  const invoiceFileInputRef = useRef(null);

  // Signature pad state and ref
  const sigCanvas = useRef(null);
  const [isSigned, setIsSigned] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [currentDate] = useState(new Date().toLocaleDateString());
  const [perjuryCertified, setPerjuryCertified] = useState(false);

  // Loading state for API
  const [loadingRetailers, setLoadingRetailers] = useState(false);
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [submissionData, setSubmissionData] = useState(null);
  const [signatureDataURL, setSignatureDataURL] = useState('');

  // Helper method to get file type classification
  const getFileType = (file) => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('application/pdf')) return 'pdf';
    if (file.type.includes('spreadsheet') || file.name.match(/\.(xlsx|xls|csv)$/i)) return 'spreadsheet';
    if (file.type.includes('document') || file.name.match(/\.(doc|docx|txt|rtf)$/i)) return 'document';
    if (file.type.includes('presentation') || file.name.match(/\.(ppt|pptx)$/i)) return 'presentation';
    if (file.type.includes('zip') || file.name.match(/\.(zip|rar|7z)$/i)) return 'archive';
    return 'other';
  };

  // Helper method to get appropriate icon for file type
  const getFileIconURL = (file) => {
    const fileType = getFileType(file);
    const iconMap = {
      pdf: '📄',
      spreadsheet: '📊',
      document: '📝',
      presentation: '📑',
      archive: '📦',
      other: '📎'
    };
    return iconMap[fileType] || '📎';
  };

  // Helper method to get file preview URL
  const getFilePreviewURL = (file) => {
    // For images, create object URL for preview
    if (file.type.startsWith('image/')) {
      return URL.createObjectURL(file);
    }
    // For other file types, return a generic icon based on file type
    return getFileIconURL(file);
  };

  // Handle invoice file selection for multiple files - SUPPORTS ALL FILE TYPES
  const handleInvoiceFileChange = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      // Accept all file types - removed image type restrictions
      const validFiles = files.filter(file => {
        // Basic file size check (50MB limit)
        return file.size <= 50 * 1024 * 1024;
      });

      if (validFiles.length > 0) {
        // Add unique IDs to each file for management with fileType field
        const filesWithIds = validFiles.map(file => ({
          id: Math.random().toString(36).substr(2, 9),
          file,
          preview: getFilePreviewURL(file),
          type: getFileType(file),
          fileType: 'Invoice' // Default file type
        }));

        setSelectedFiles(prev => [...prev, ...filesWithIds]);
        setUploadStatus('');

        if (validFiles.length < files.length) {
          setUploadStatus('Some files were skipped due to size limits (max 50MB per file).');
        }
      } else {
        setUploadStatus('Please select valid files (max 50MB per file)');
      }
    }
  };

  // Handle drag and drop for multiple invoice files
  const handleInvoiceDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) {
      handleInvoiceFileChange({ target: { files } });
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  // Handle file type change for uploaded files
  const handleFileTypeChange = (fileId, fileType) => {
    setSelectedFiles(prev =>
      prev.map(fileObj =>
        fileObj.id === fileId
          ? { ...fileObj, fileType }
          : fileObj
      )
    );
  };

  // Remove single invoice file
  const handleRemoveInvoiceFile = (fileId) => {
    setSelectedFiles(prev => {
      const fileToRemove = prev.find(f => f.id === fileId);
      if (fileToRemove && fileToRemove.file.type.startsWith('image/')) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter(f => f.id !== fileId);
    });
  };

  // Remove all invoice files
  const handleRemoveAllInvoiceFiles = () => {
    selectedFiles.forEach(file => {
      if (file.file.type.startsWith('image/')) {
        URL.revokeObjectURL(file.preview);
      }
    });
    setSelectedFiles([]);
    setUploadStatus('');
  };

  // File Preview Component
  const FilePreview = ({ fileObj }) => {
    if (fileObj.type === 'image') {
      return (
        <img
          src={fileObj.preview}
          alt={`Invoice ${fileObj.file.name}`}
          className="w-full h-full object-cover"
        />
      );
    } else {
      return (
        <div className="w-full h-full flex items-center justify-center bg-gray-100">
          <div className="text-center p-2">
            <div className="text-2xl mb-1">{fileObj.preview}</div>
            <div className="text-xs text-gray-600 truncate">{fileObj.file.name}</div>
          </div>
        </div>
      );
    }
  };

  // Get taxpayer ID and business name from localStorage on component mount
  useEffect(() => {
    const storedTaxpayerId = localStorage.getItem("selectedTaxPayer");
    const storedBusinessName = localStorage.getItem("selectedBusinessName");
    const storedBusinessAddress = localStorage.getItem("businessAddress");

    if (storedTaxpayerId) {
      setTaxpayerId(storedTaxpayerId);
      console.log('Retrieved taxpayer ID from localStorage:', storedTaxpayerId);
    } else {
      console.warn('No taxpayer ID found in localStorage');
      // Fallback to default if not found
      setTaxpayerId('109');
    }

    if (storedBusinessName) {
      setBusinessName(storedBusinessName);
      // Update distributor info with the business name from localStorage
      setFormData(prev => ({
        ...prev,
        distributorName: storedBusinessName
      }));
      console.log('Retrieved business name from localStorage:', storedBusinessName);
    } else {
      console.warn('No business name found in localStorage');
      // Fallback to default if not found
      setFormData(prev => ({
        ...prev,
        distributorName: 'Cougar Den'
      }));
    }

    // Set distributor address from localStorage businessAddress
    if (storedBusinessAddress) {
      setFormData(prev => ({
        ...prev,
        distributorAddress: storedBusinessAddress
      }));
      console.log('Retrieved business address from localStorage:', storedBusinessAddress);
    } else {
      console.warn('No business address found in localStorage');
    }
  }, []);

  // Initialize report date on component mount
  useEffect(() => {
    const defaultDate = getDefaultReportDate();
    setReportInfo({
      month: defaultDate.month,
      year: defaultDate.year
    });
  }, []);

  // Fetch retailers from API on component mount
  useEffect(() => {
    fetchRetailers();
  }, []);

  // API function to fetch retailers
  const fetchRetailers = async () => {
    try {
      setLoadingRetailers(true);
      setApiError('');

      // Get taxpayer ID from localStorage or use default
      const taxpayerIdToUse = taxpayerId || '109';

      const apiUrl = PBPUTILS + `?taxpayer=${taxpayerIdToUse}&action=getretailers`;

      console.log('Fetching retailers from:', apiUrl);

      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      console.log('API Response:', data);

      // Handle the new response format
      let retailers = [];
      let retailerFullData = [];

      if (data.status === 'success' && Array.isArray(data.data)) {
        // Store full retailer data including sval
        retailerFullData = data.data.filter(item => item.dval && item.dval.trim() !== '');

        // Extract retailer names from the data array
        retailers = retailerFullData.map(item => item.dval);

        if (retailers.length > 0) {
          setAvailableRetailers(retailers);
          setRetailerData(retailerFullData);
          // Set the first retailer as default selected
          setFormData(prev => ({
            ...prev,
            selectedRetailer: retailers[0],
            selectedRetailerId: retailerFullData[0]?.sval?.toString() || ''
          }));
          console.log('Successfully loaded retailers:', retailers);
        } else {
          setAvailableRetailers(['One Stop', 'Cougar Den', 'Other']);
          setFormData(prev => ({ ...prev, selectedRetailer: 'One Stop' }));
          console.warn('No valid retailer names found in API response, using defaults');
        }
      } else {
        throw new Error('Invalid API response format');
      }

    } catch (error) {
      console.error('Error fetching retailers:', error);
      setApiError(`Failed to load retailers: ${error.message}`);

      // Fallback to default retailers
      setAvailableRetailers(['One Stop', 'Cougar Den', 'Other']);
      setFormData(prev => ({ ...prev, selectedRetailer: 'One Stop' }));
    } finally {
      setLoadingRetailers(false);
    }
  };

  // Initialize canvas size after component mounts
  useEffect(() => {
    const updateCanvasSize = () => {
      const container = document.getElementById('signature-container');
      if (container) {
        const width = container.clientWidth - 32;
        const height = 200;
        setCanvasSize({ width, height });

        if (sigCanvas.current) {
          const isEmpty = sigCanvas.current.isEmpty();
          if (!isEmpty) {
            const data = sigCanvas.current.toData();
            sigCanvas.current.clear();
            setTimeout(() => {
              if (sigCanvas.current) {
                sigCanvas.current.fromData(data);
              }
            }, 0);
          }
        }
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle retailer selection change
  const handleRetailerSelectionChange = (e) => {
    const selectedName = e.target.value;
    const selectedRetailer = retailerData.find(item => item.dval === selectedName);

    setFormData(prev => ({
      ...prev,
      selectedRetailer: selectedName,
      selectedRetailerId: selectedRetailer?.sval?.toString() || ''
    }));
  };

  const handleRetailerChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      retailers: prev.retailers.map((retailer, i) =>
        i === index ? { ...retailer, [field]: value } : retailer
      )
    }));
  };

  // Add new retailer from dropdown
  const addRetailer = () => {
    if (formData.selectedRetailer && formData.selectedRetailerId) {
      const newRetailer = {
        name: formData.selectedRetailer,
        id: formData.selectedRetailerId,
        gasGallons: '',
        gasTax: '',
        dieselGallons: '',
        dieselTax: '',
        totalTax: ''
      };

      setFormData(prev => ({
        ...prev,
        retailers: [...prev.retailers, newRetailer]
      }));
    }
  };

  // Remove retailer
  const removeRetailer = (index) => {
    setFormData(prev => ({
      ...prev,
      retailers: prev.retailers.filter((_, i) => i !== index)
    }));
  };

  // Handle report information changes
  const handleReportInfoChange = (field, value) => {
    setReportInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Calculate totals dynamically from retailer data
  const calculateTotalGasGallons = () => {
    return formData.retailers.reduce((total, retailer) => {
      return total + (parseFloat(retailer.gasGallons) || 0);
    }, 0);
  };

  const calculateTotalGasTax = () => {
    return formData.retailers.reduce((total, retailer) => {
      return total + (parseFloat(retailer.gasTax) || 0);
    }, 0);
  };

  const calculateTotalDieselGallons = () => {
    return formData.retailers.reduce((total, retailer) => {
      return total + (parseFloat(retailer.dieselGallons) || 0);
    }, 0);
  };

  const calculateTotalDieselTax = () => {
    return formData.retailers.reduce((total, retailer) => {
      return total + (parseFloat(retailer.dieselTax) || 0);
    }, 0);
  };

  const calculateTotalCombinedGallons = () => {
    return calculateTotalGasGallons() + calculateTotalDieselGallons();
  };

  const calculateTotalTax = () => {
    return formData.retailers.reduce((total, retailer) => {
      return total + (parseFloat(retailer.totalTax) || 0);
    }, 0);
  };

  // Auto-calculate tax when gallons are entered (5 cents per gallon)
  const calculateTax = (gallons) => {
    return (parseFloat(gallons) * 0.05).toFixed(2) || '0.00';
  };

  // Auto-calculate total tax when gas and diesel tax are entered
  const calculateTotalTaxForRetailer = (gasTax, dieselTax) => {
    const gas = parseFloat(gasTax) || 0;
    const diesel = parseFloat(dieselTax) || 0;
    return (gas + diesel).toFixed(2);
  };

  // Format number with commas for display
  const formatNumberWithCommas = (value) => {
    if (!value && value !== 0) return '0';

    const number = parseFloat(value);
    if (isNaN(number)) return '0';

    // Check if it's a decimal number
    if (number % 1 !== 0) {
      return number.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    } else {
      return number.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    }
  };

  // Handle gallon input with auto-tax calculation
  const handleGallonInput = (index, fuelType, value) => {
    const gallons = value;
    const tax = calculateTax(gallons);

    if (fuelType === 'gas') {
      handleRetailerChange(index, 'gasGallons', gallons);
      handleRetailerChange(index, 'gasTax', tax);
    } else {
      handleRetailerChange(index, 'dieselGallons', gallons);
      handleRetailerChange(index, 'dieselTax', tax);
    }

    // Recalculate total tax for this retailer
    const retailer = formData.retailers[index];
    const newGasTax = fuelType === 'gas' ? tax : (retailer.gasTax || '0');
    const newDieselTax = fuelType === 'diesel' ? tax : (retailer.dieselTax || '0');
    const totalTax = calculateTotalTaxForRetailer(newGasTax, newDieselTax);
    handleRetailerChange(index, 'totalTax', totalTax);
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format number
  const formatNumber = (value) => {
    return isNaN(value) ? 0 : parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  // Format number with decimals
  const formatNumberWithDecimals = (value) => {
    return isNaN(value) ? '0.00' : parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;
  };

  // Helper function to convert base64 data URL to File object
  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  // Add this helper function to convert PDF to Blob
  const pdfToBlob = (pdf) => {
    const pdfOutput = pdf.output('blob');
    return pdfOutput;
  };

  const generatePDF = (signatureDataOverride = null) => {
    const pdf = new jsPDF();

    // Set font and colors
    pdf.setFont('helvetica');
    pdf.setTextColor(0, 0, 0); // #840028

    // Header Section
    pdf.setFontSize(16);
    pdf.text('Monthly Motor Fuel Distributor Report', 105, 20, { align: 'center' });

    pdf.setFontSize(12);
    pdf.text('Prairie Band Potawatomi Tax Commission', 105, 30, { align: 'center' });
    pdf.text('16281 Q Road', 105, 36, { align: 'center' });
    pdf.text('Mayetta, KS 66509', 105, 42, { align: 'center' });

    pdf.setFontSize(10);
    pdf.text('Reports and Payment due on the 10th of each Month', 105, 50, { align: 'center' });

    // Report Information
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Report Information:', 20, 65);
    pdf.setFontSize(10);
    pdf.text(`Month of Report: ${reportInfo.month} ${reportInfo.year}`, 20, 72);
    pdf.text(`Report Type: ${formData.reportType === 'original' ? 'Original Report' : 'Amended Report'}`, 20, 78);

    if (formData.reportType === 'amended' && formData.amendedMonth && formData.amendedYear) {
      pdf.text(`Amended for: ${formData.amendedMonth} ${formData.amendedYear}`, 20, 84);
    }

    // Distributor Information
    pdf.text(`Distributor Name: ${formData.distributorName}`, 20, 92);
    if (formData.distributorAddress) {
      pdf.text(`Address: ${formData.distributorAddress}`, 20, 98);
    }

    // Taxpayer Information
    if (taxpayerId) {
      pdf.text(`Taxpayer ID: ${taxpayerId}`, 20, 104);
    }

    // Retailer Data Section
    let yPosition = 122;

    if (formData.retailers.length > 0) {
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Monthly MOTOR FUEL DISTRIBUTOR REPORT', 20, yPosition);
      yPosition += 10;

      // Table Header
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.setFillColor(240, 240, 240);
      pdf.rect(20, yPosition, 184, 8, 'F');
      pdf.text('Retailer', 25, yPosition + 6);
      pdf.text('Gas Gallons', 85, yPosition + 6, { align: 'right' });
      pdf.text('Gas Tax', 115, yPosition + 6, { align: 'right' });
      pdf.text('Diesel Gallons', 145, yPosition + 6, { align: 'right' });
      pdf.text('Diesel Tax', 175, yPosition + 6, { align: 'right' });
      pdf.text('Total Tax', 200, yPosition + 6, { align: 'right' });

      yPosition += 12;

      // Retailer Rows (wrap long retailer names to multiple lines)
      formData.retailers.forEach((retailer, index) => {
        // Determine column metrics - reduced retailer column width
        const retailerColX = 25;
        const gasColX = 65;
        const retailerColWidth = gasColX - retailerColX - 3; // leave small padding (now 37 units wide for multiline wrapping)
        const lineHeight = 6;

        const retailerName = retailer.name || '';
        const retailerLines = pdf.splitTextToSize(retailerName, retailerColWidth);
        const requiredHeight = Math.max(lineHeight, retailerLines.length * lineHeight);

        if (yPosition + requiredHeight > 270) {
          pdf.addPage();
          yPosition = 20;
          // Add header again on new page
          pdf.setFillColor(240, 240, 240);
          pdf.rect(20, yPosition, 180, 8, 'F');
          pdf.text('Retailer', 25, yPosition + 6);
          pdf.text('Gas Gallons', 85, yPosition + 6, { align: 'right' });
          pdf.text('Gas Tax', 115, yPosition + 6, { align: 'right' });
          pdf.text('Diesel Gallons', 145, yPosition + 6, { align: 'right' });
          pdf.text('Diesel Tax', 175, yPosition + 6, { align: 'right' });
          pdf.text('Total Tax', 200, yPosition + 6, { align: 'right' });
          yPosition += 12;
        }

        // Draw retailer name lines (stacked vertically)
        retailerLines.forEach((line, i) => {
          pdf.text(line, retailerColX, yPosition + (i * lineHeight));
        });

        // Draw numeric columns aligned to the first line of the retailer name
        pdf.text(formatNumberWithDecimals(retailer.gasGallons || 0), 85, yPosition, { align: 'right' });
        pdf.text(`$${formatCurrency(retailer.gasTax || 0)}`, 115, yPosition, { align: 'right' });
        pdf.text(formatNumberWithDecimals(retailer.dieselGallons || 0), 145, yPosition, { align: 'right' });
        pdf.text(`$${formatCurrency(retailer.dieselTax || 0)}`, 175, yPosition, { align: 'right' });
        pdf.text(`$${formatCurrency(retailer.totalTax || 0)}`, 200, yPosition, { align: 'right' });

        // Move yPosition by required height plus a small padding
        yPosition += requiredHeight + 2;
      });

      // Totals Section
      if (yPosition > 250) {
        pdf.addPage();
        yPosition = 20;
      }

      yPosition += 10;
      pdf.setFontSize(11);
      pdf.setFont(undefined, 'bold');
      pdf.text('TOTALS:', 25, yPosition);
      pdf.text(formatNumber(calculateTotalGasGallons()), 85, yPosition, { align: 'right' });
      pdf.text(`$${formatCurrency(calculateTotalGasTax())}`, 115, yPosition, { align: 'right' });
      pdf.text(formatNumber(calculateTotalDieselGallons()), 145, yPosition, { align: 'right' });
      pdf.text(`$${formatCurrency(calculateTotalDieselTax())}`, 175, yPosition, { align: 'right' });
      pdf.text(`$${formatCurrency(calculateTotalTax())}`, 200, yPosition, { align: 'right' });

      yPosition += 15;
      pdf.setFont(undefined, 'normal');
      pdf.text(`Total Combined Gallons: ${formatNumber(calculateTotalCombinedGallons())}`, 25, yPosition);
      yPosition += 8;

      // Payment Information - Labels outside boxes, values centered in boxes
      const tableStartY = yPosition;
      const cellWidth = 30;
      const cellHeight = 6;

      // Draw table borders
      pdf.setDrawColor(0, 0, 0); // Black border
      pdf.setLineWidth(0.5);

      // Total Paid box (top)
      pdf.rect(65, tableStartY, cellWidth, cellHeight);

      // Check Date box (bottom)
      pdf.rect(65, tableStartY + cellHeight, cellWidth, cellHeight);

      // Payment Information content
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);

      // Total Paid - Label outside box, value centered in box
      pdf.text('Total Paid:', 25, tableStartY + 4);

      // Calculate center position for Total Paid value (both vertical and horizontal)
      const totalPaidCenterY = tableStartY + (cellHeight / 2) + 1;
      const totalPaidCenterX = 65 + (cellWidth / 2);
      pdf.setFont(undefined, 'bold');
      pdf.text(`$${formatCurrency(calculateTotalTax())}`, totalPaidCenterX, totalPaidCenterY, { align: 'center' });

      // Check Date - Label outside box, value centered in box
      pdf.setFont(undefined, 'normal');
      pdf.text('Check Date:', 25, tableStartY + cellHeight + 4);

      // Calculate center position for Check Date value (both vertical and horizontal)
      const checkDateCenterY = tableStartY + cellHeight + (cellHeight / 2) + 1;
      const checkDateCenterX = 65 + (cellWidth / 2);
      // Ensure non-total values remain normal weight
      pdf.setFont(undefined, 'normal');

      // Format check date properly (MM/DD/YY format)
      let displayCheckDate = currentDate; // Default to current date
      if (formData.checkDate) {
        const checkDate = new Date(formData.checkDate);
        displayCheckDate = `${String(checkDate.getMonth() + 1).padStart(2, '0')}/${String(checkDate.getDate()).padStart(2, '0')}/${checkDate.getFullYear().toString().slice(-2)}`;
      } else {
        // Format current date as MM/DD/YY if no check date provided
        const today = new Date();
        displayCheckDate = `${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}/${today.getFullYear().toString().slice(-2)}`;
      }

      pdf.text(displayCheckDate, checkDateCenterX, checkDateCenterY, { align: 'center' });

      yPosition = tableStartY + (cellHeight * 2) + 5; // Move yPosition below both boxes

      // Additional payment information
      if (formData.numberOfInvoices) {
        pdf.setFont(undefined, 'normal');
        pdf.text(`Total number of invoices for the month: ${formData.numberOfInvoices}`, 25, yPosition);
        yPosition += 8;
      }
    } else {
      pdf.setFontSize(11);
      pdf.text('No retailer data entered', 20, yPosition);
      yPosition += 15;
    }

    // ADDED: Tax Rate Information Section (from the image)
    if (yPosition > 220) {
      pdf.addPage();
      yPosition = 20;
    }

    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);

    // Tax rate information
    pdf.text('The Excise tax rate is $.05 per gallon on all motor fuels.', 20, yPosition);
    yPosition += 8;

    // Report and payment due dates
    pdf.text('REPORTS ARE DUE 10 Calendar Days after the end of each calendar month.', 20, yPosition);
    yPosition += 6;
    pdf.text('PAYMENTS ARE DUE 14 Calendar Days after the end of each calendar month.', 20, yPosition);
    yPosition += 8;

    // Submission information
    pdf.text('This report is to be submitted to: taxcommission@pbpnation.org or mailed to:', 20, yPosition);
    yPosition += 6;
    pdf.text('Prairie Band Potawatomi Tax Commission, 16281 Q Road, Mayetta, KS 66509. 785.966.3999', 20, yPosition);
    yPosition += 6;
    pdf.text('Credit Card or ACH Payments can be made via the PBPN Bill Pay Portal at the link below.', 20, yPosition);
    yPosition += 6;
    pdf.text('https://pay.imsmerchantportal.com/motorfueldistributorretailerlicense', 20, yPosition);
    yPosition += 12;

    // Signature and Certification
    if (yPosition > 220) {
      pdf.addPage();
      yPosition = 20;
    }

    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Signature and Certification', 105, yPosition, { align: 'center' });
    yPosition += 10;

    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);

    // No Sales Certification
    if (formData.noSales) {
      pdf.text('I certify my business did not sell or make any Motor Fuel deliveries within the PBPN jurisdiction during  this filing month.', 20, yPosition);
      yPosition += 15;
    }

    pdf.text('I declare under penalty of perjury under the PBPN law and order codes that the foregoing is true and correct.', 20, yPosition);
    yPosition += 15;

    // Add signature image if available
    const signatureToUse = signatureDataOverride || signatureDataURL;
    if (signatureToUse) {
      try {
        pdf.text('Signature:', 20, yPosition);
        yPosition += 8;

        const signatureWidth = 80;
        const signatureHeight = 30;
        pdf.addImage(signatureToUse, 'PNG', 20, yPosition, signatureWidth, signatureHeight);
        yPosition += signatureHeight + 15;
      } catch (error) {
        console.error('Error adding signature to PDF:', error);
        pdf.text('Signature: Provided during submission', 20, yPosition);
        yPosition += 10;
      }
    } else {
      pdf.text('Signature: Not provided', 20, yPosition);
      yPosition += 10;
    }

    if (printedName) {
      pdf.text(`Printed Name: ${printedName}`, 20, yPosition);
      yPosition += 8;
    }
    if (title) {
      pdf.text(`Title: ${title}`, 20, yPosition);
      yPosition += 8;
    }

    pdf.text(`Date Signed: ${currentDate}`, 20, yPosition);

    return pdf;
  };

  // Format month for API (convert to number with leading zero)
  const formatMonthForAPI = (monthName) => {
    const monthMap = {
      'January': '01', 'February': '02', 'March': '03', 'April': '04',
      'May': '05', 'June': '06', 'July': '07', 'August': '08',
      'September': '09', 'October': '10', 'November': '11', 'December': '12'
    };
    return monthMap[monthName] || '01';
  };

  // Helper function to convert FormData to object for debugging
  const formDataToObject = (formData) => {
    const object = {};
    for (const [key, value] of formData.entries()) {
      if (key === 'Signature' || key === 'PDF' || key === 'Invoices') {
        object[key] = `[File Blob - ${value.size} bytes]`;
      } else if (key === 'Body') {
        try {
          object[key] = JSON.parse(value);
        } catch (e) {
          object[key] = value;
        }
      } else {
        object[key] = value;
      }
    }
    return object;
  };

  // Function to handle PDF download from SuccessPage
  const handleDownloadPDF = () => {
    const pdf = generatePDF();
    const fileName = `MotorFuel_Report_${reportInfo.month}_${reportInfo.year}_${formData.distributorName.replace(/\s+/g, '_')}.pdf`;
    pdf.save(fileName);
  };

  // Function to handle back to form
  const handleBackToForm = () => {
    setShowSuccessScreen(false);
  };

  // Updated handleSubmit function with API implementation and SuccessPage
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!perjuryCertified) {
      alert('Please check the certification checkbox next to "I declare under penalty of perjury" before submitting.');
      return;
    }

    if (!isSigned || (sigCanvas.current && sigCanvas.current.isEmpty())) {
      alert('Please provide a signature before submitting the report.');
      return;
    }

    if (!printedName.trim()) {
      alert('Please enter your printed name.');
      return;
    }

    if (!title.trim()) {
      alert('Please enter your title.');
      return;
    }

    if (selectedFiles.length === 0) {
      alert('Please upload at least one file (Invoice or Bill of Lading).');
      return;
    }

    try {
      setSubmitting(true);

      // Get signature data and store it for PDF generation
      const signatureData = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
      setSignatureDataURL(signatureData);

      // Generate PDF
      const pdf = generatePDF(signatureData);

      // Convert PDF to Blob and then to File
      const pdfBlob = pdfToBlob(pdf);
      const pdfFile = new File([pdfBlob], `MotorFuel_Report_${reportInfo.month}_${reportInfo.year}_${formData.distributorName.replace(/\s+/g, '_')}.pdf`, {
        type: 'application/pdf'
      });

      // Get taxpayer ID from localStorage or use default
      const taxpayerIdToUse = taxpayerId || '109';

      // Format date for API
      const formattedMonth = formatMonthForAPI(reportInfo.month);
      const dateParam = `${formattedMonth}-${reportInfo.year}`;

      // Prepare distributed data in the required JSON format
      const distributedData = formData.retailers.map(retailer => ({
        retailer: parseInt(retailer.id) || 0,
        gasgallons: parseFloat(retailer.gasGallons) || 0,
        gastax: parseFloat(retailer.gasTax) || 0,
        dieselgallons: parseFloat(retailer.dieselGallons) || 0,
        dieseltax: parseFloat(retailer.dieselTax) || 0
      }));

      // Prepare the request body
      const requestBody = {
        distributed: distributedData,
        checkdate: formData.checkDate || currentDate
      };

      // Prepare form data
      const formDataToSend = new FormData();

      // Add URL parameters as form data
      formDataToSend.append('taxpayer', taxpayerIdToUse);
      formDataToSend.append('reporttype', 'fuel');
      formDataToSend.append('test', 'true');

      // Add report month and year
      formDataToSend.append('reportMonth', reportInfo.month);
      formDataToSend.append('reportYear', reportInfo.year);

      // Add the JSON body
      formDataToSend.append('Body', JSON.stringify(requestBody));

      // Add printed name and title
      formDataToSend.append('printedName', printedName);
      formDataToSend.append('title', title);

      // Add signature
      const signatureBlob = await fetch(signatureData).then(res => res.blob());
      formDataToSend.append('Signature', signatureBlob, 'signature.png');

      // Append PDF to FormData
      formDataToSend.append('PDF', pdfFile);

      // Add all invoice files to 'Receipts' key with their file types
      selectedFiles.forEach((fileObj, index) => {
        formDataToSend.append('Receipts', fileObj.file);
        // Append file type with index to match files with their types
        formDataToSend.append(`ReceiptsType_${index}`, fileObj.fileType || 'Invoice');
      });

      // Store submission data for PDF generation
      setSubmissionData({
        formData,
        reportInfo,
        totals: {
          totalGasGallons: calculateTotalGasGallons(),
          totalGasTax: calculateTotalGasTax(),
          totalDieselGallons: calculateTotalDieselGallons(),
          totalDieselTax: calculateTotalDieselTax(),
          totalCombinedGallons: calculateTotalCombinedGallons(),
          totalTax: calculateTotalTax()
        },
        printedName,
        title,
        signatureData
      });

      // Print form data for debugging
      console.log('=== FORM DATA TO SEND ===');
      const formDataObject = formDataToObject(formDataToSend);
      console.log('FormData contents:', JSON.stringify(formDataObject, null, 2));
      console.log('=== END FORM DATA ===');

      // Make API call
      const apiUrl = PBPUTILS + `?taxpayer=${taxpayerIdToUse}&reporttype=fuel&test=true`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formDataToSend
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.text();
      let result;
      result = JSON.parse(responseData);
      console.log('API Response:', responseData);

      if (result && result.Status === 'Success') {
        setShowSuccessScreen(true);
        return;
      } else if (result && result.Status === 'Error') {
        alert(result.Message || 'An error occurred while submitting the report.');
        return;
      } else {
        throw new Error('Unexpected response format from server');
      }

    } catch (error) {
      console.error('Error submitting report:', error);
      alert(`Error submitting report: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Signature functions
  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setIsSigned(false);
    }
  };

  const handleSignatureBegin = () => {
    setIsSigned(true);
  };

  const handleSignatureEnd = () => {
    // Signature completion logic
  };

  // Prepare report details for SuccessPage
  const reportDetails = {
    'Report Type': 'Motor Fuel Distributor Report',
    'Taxpayer ID': taxpayerId,
    'Business': businessName,
    'Period': `${reportInfo.month} ${reportInfo.year}`,
    'Submitted': currentDate,
    'Total Tax Paid': `$${formatCurrency(calculateTotalTax())}`,
    'Invoices Uploaded': selectedFiles.length > 0 ? 'Yes' : 'No'
  };

  // If showing success screen, render the SuccessPage component
  if (showSuccessScreen) {
    return (
      <SuccessPage
        title="Motor Fuel Distributor Report Submitted Successfully!"
        subtitle={`Your motor fuel distributor report for ${reportInfo.month} ${reportInfo.year} has been successfully submitted.`}
        downloadButtonText="Download Motor Fuel Report PDF"
        backButtonText="Back to Home"
        onDownload={handleDownloadPDF}
        onBack={handleBackToForm}
        reportDetails={reportDetails}
        contactInfo={{
          phone: "(785) 966-3060",
          email: "taxcommission@pbpnation.org",
          department: "Prairie Band Potawatomi Tax Commission"
        }}
      >
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="font-semibold text-green-800 mb-2">Important Notes:</h3>
          <ul className="text-sm text-green-700 space-y-1">
            <li>• The report covers motor fuel distribution for the specified period</li>
            <li>• Contact the Tax Commission staff if you need to make any corrections</li>
            <li>• {selectedFiles.length} invoice{selectedFiles.length !== 1 ? 's' : ''} were included with your submission</li>
          </ul>
        </div>
      </SuccessPage>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 bg-white">
      {/* Header */}
      <div className="text-center mb-6 sm:mb-8 border-b-2 border-[#840028] pb-4 sm:pb-6">
        <h1 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#840028]">
          Prairie Band Potawatomi Tax Commission
        </h1>
        <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-[#840028]">
          Monthly Motor Fuel Distributor Report
        </h2>
        {taxpayerId && (
          <div className="text-xs sm:text-sm text-gray-600 mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-4">
            <span>Taxpayer ID: <strong>{taxpayerId}</strong></span>
            {businessName && (
              <span>Business: <strong>{businessName}</strong></span>
            )}
          </div>
        )}
      </div>

      {/* Form ID */}
      <div className="bg-gray-100 px-4 sm:px-6 py-2 border-b">
        <div className="flex justify-between items-center">
          <span className="font-semibold text-sm sm:text-base">Motor Fuel Distributor Report</span>
          <span className="text-gray-600 text-xs sm:text-sm">Page 1</span>
        </div>
      </div>

      {/* Report Information Section */}
      <div className="border border-gray-300 p-3 sm:p-4 m-4 sm:m-6 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0 mb-3 sm:mb-4">
          <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">Month of Report:</label>
          <div className="flex items-center space-x-2 sm:space-x-4">
            <select
              value={reportInfo.month}
              onChange={(e) => handleReportInfoChange('month', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-32 text-sm sm:text-base"
            >
              {monthOptions.map(month => (
                <option key={month} value={month}>{month}</option>
              ))}
            </select>
            <select
              value={reportInfo.year}
              onChange={(e) => handleReportInfoChange('year', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-20 text-sm sm:text-base"
            >
              {yearOptions.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
            <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">Check One:</label>
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="reportType"
                  value="original"
                  checked={formData.reportType === 'original'}
                  onChange={handleInputChange}
                  className="mr-2"
                />
                <span className="text-sm sm:text-base">Original Report</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="reportType"
                  value="amended"
                  checked={formData.reportType === 'amended'}
                  onChange={handleInputChange}
                  className="mr-2"
                />
                <span className="text-sm sm:text-base">Amended Report</span>
              </label>
            </div>
          </div>
          {formData.reportType === 'amended' && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
              <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">Filing Month/Year:</label>
              <div className="flex items-center space-x-2">
                <select
                  value={formData.amendedMonth}
                  onChange={(e) => setFormData(prev => ({ ...prev, amendedMonth: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm sm:text-base"
                >
                  <option value="">Month</option>
                  {monthOptions.map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
                <select
                  value={formData.amendedYear}
                  onChange={(e) => setFormData(prev => ({ ...prev, amendedYear: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm sm:text-base"
                >
                  <option value="">Year</option>
                  {yearOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
            <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">Distributor Name:</label>
            <input
              type="text"
              name="distributorName"
              value={formData.distributorName}
              onChange={handleInputChange}
              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white flex-1 text-sm sm:text-base"
            />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
            <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">Address, City, State, Zip:</label>
            <input
              type="text"
              name="distributorAddress"
              value={formData.distributorAddress}
              onChange={handleInputChange}
              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white flex-1 text-sm sm:text-base"
              placeholder="Enter full address"
            />
          </div>
        </div>
      </div>

      {/* Main Form Content */}
      <div className="p-4 sm:p-6">
        {/* Add Retailer Section */}
        <div className="mb-4 sm:mb-6 p-3 sm:p-4 border border-gray-300 rounded-lg bg-blue-50">
          <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-800">
            <span className="text-black">Retailer(s)</span>
            <span className="text-gray-500 ml-2"> Select Retailer and click add row to enter return.</span>
          </h3>

          {/* API Status */}
          {loadingRetailers && (
            <div className="mb-2 text-blue-600 text-xs sm:text-sm">
              Loading retailers from API...
            </div>
          )}

          {apiError && (
            <div className="mb-2 text-red-600 text-xs sm:text-sm">
              {apiError} (using default retailers)
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            <select
              value={formData.selectedRetailer}
              onChange={handleRetailerSelectionChange}
              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-48 text-sm sm:text-base"
              disabled={loadingRetailers || availableRetailers.length === 0}
            >
              {availableRetailers.map(retailer => (
                <option key={retailer} value={retailer}>{retailer}</option>
              ))}
            </select>

            <button
              type="button"
              onClick={addRetailer}
              disabled={!formData.selectedRetailer || loadingRetailers}
              className="px-4 py-2 bg-[#840028] text-white rounded hover:bg-[#6a0020] transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm sm:text-base"
            >
              {loadingRetailers ? 'Loading...' : 'Add Row'}
            </button>

            <button
              type="button"
              onClick={fetchRetailers}
              disabled={loadingRetailers}
              className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition duration-200 text-xs sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loadingRetailers ? 'Refreshing...' : 'Refresh Retailers'}
            </button>
          </div>

          {availableRetailers.length === 0 && !loadingRetailers && (
            <div className="mt-2 text-yellow-600 text-xs sm:text-sm">
              No retailers available. Please refresh or check your connection.
            </div>
          )}
        </div>

        {/* Retailer Table */}
        {formData.retailers.length > 0 && (
          <div className="overflow-x-auto mb-6 sm:mb-8">
            <table className="min-w-full border border-gray-300 text-sm sm:text-base">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-2 sm:px-4 py-2 text-left font-semibold text-xs sm:text-sm">Name of Retailer</th>
                  <th className="border border-gray-300 px-2 sm:px-4 py-2 text-right font-semibold text-xs sm:text-sm">Gas Gallons</th>
                  <th className="border border-gray-300 px-2 sm:px-4 py-2 text-right font-semibold text-xs sm:text-sm">Gas Tax</th>
                  <th className="border border-gray-300 px-2 sm:px-4 py-2 text-right font-semibold text-xs sm:text-sm">Diesel Gallons</th>
                  <th className="border border-gray-300 px-2 sm:px-4 py-2 text-right font-semibold text-xs sm:text-sm">Diesel Tax</th>
                  <th className="border border-gray-300 px-2 sm:px-4 py-2 text-right font-semibold text-xs sm:text-sm">Total Tax</th>
                  <th className="border border-gray-300 px-2 sm:px-4 py-2 text-center font-semibold text-xs sm:text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {formData.retailers.map((retailer, index) => (
                  <tr key={index}>
                    <td className="border border-gray-300 px-2 sm:px-4 py-2 font-semibold text-xs sm:text-sm">
                      {retailer.name}
                    </td>
                    <td className="border border-gray-300 px-1 sm:px-2 py-1">
                      <input
                        type="text"
                        value={retailer.gasGallons ? formatNumberWithCommas(retailer.gasGallons) : ''}
                        onChange={(e) => {
                          // Remove commas for processing, keep only numbers and decimal
                          const rawValue = e.target.value.replace(/,/g, '');
                          if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                            handleGallonInput(index, 'gas', rawValue);
                          }
                        }}
                        onBlur={(e) => {
                          // Format on blur
                          const rawValue = e.target.value.replace(/,/g, '');
                          if (rawValue !== '') {
                            handleGallonInput(index, 'gas', rawValue);
                          }
                        }}
                        className="w-full px-1 sm:px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right text-xs sm:text-sm"
                        placeholder="0"
                      />
                    </td>
                    <td className="border border-gray-300 px-2 sm:px-4 py-2 text-right text-xs sm:text-sm">
                      ${retailer.gasTax ? formatNumberWithCommas(retailer.gasTax) : '0.00'}
                    </td>
                    <td className="border border-gray-300 px-1 sm:px-2 py-1">
                      <input
                        type="text"
                        value={retailer.dieselGallons ? formatNumberWithCommas(retailer.dieselGallons) : ''}
                        onChange={(e) => {
                          const rawValue = e.target.value.replace(/,/g, '');
                          if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                            handleGallonInput(index, 'diesel', rawValue);
                          }
                        }}
                        onBlur={(e) => {
                          const rawValue = e.target.value.replace(/,/g, '');
                          if (rawValue !== '') {
                            handleGallonInput(index, 'diesel', rawValue);
                          }
                        }}
                        className="w-full px-1 sm:px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right text-xs sm:text-sm"
                        placeholder="0"
                      />
                    </td>
                    <td className="border border-gray-300 px-2 sm:px-4 py-2 text-right text-xs sm:text-sm">
                      ${retailer.dieselTax ? formatNumberWithCommas(retailer.dieselTax) : '0.00'}
                    </td>
                    <td className="border border-gray-300 px-2 sm:px-4 py-2 text-right font-semibold text-xs sm:text-sm">
                      ${retailer.totalTax ? formatNumberWithCommas(retailer.totalTax) : '0.00'}
                    </td>
                    <td className="border border-gray-300 px-2 sm:px-4 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => removeRetailer(index)}
                        className="px-2 sm:px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition duration-200 text-xs sm:text-sm"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Totals Section */}
        {formData.retailers.length > 0 && (
          <div className="border border-gray-300 p-4 sm:p-6 mb-6 sm:mb-8 bg-gray-50">
            <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-800">Total Gallons of Fuel</h3>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-4 mb-3 sm:mb-4 text-xs sm:text-sm">
              <div className="text-center font-semibold">Gas Gallons</div>
              <div className="text-center font-semibold">Gas Tax</div>
              <div className="text-center font-semibold">Diesel Gallons</div>
              <div className="text-center font-semibold">Diesel Tax</div>
              <div className="text-center font-semibold">Total Tax</div>

              <div className="text-center bg-white p-2 border rounded">
                {formatNumberWithCommas(calculateTotalGasGallons())}
              </div>
              <div className="text-center bg-white p-2 border rounded">
                ${formatNumberWithCommas(calculateTotalGasTax())}
              </div>
              <div className="text-center bg-white p-2 border rounded">
                {formatNumberWithCommas(calculateTotalDieselGallons())}
              </div>
              <div className="text-center bg-white p-2 border rounded">
                ${formatNumberWithCommas(calculateTotalDieselTax())}
              </div>
              <div className="text-center bg-white p-2 border rounded font-bold">
                ${formatNumberWithCommas(calculateTotalTax())}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-4 sm:mt-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
                <label className="font-semibold sm:w-64 sm:mr-4 text-gray-700 text-xs sm:text-sm">
                  Total Combined Gallons of Gas and Diesel:
                </label>
                <div className="bg-white px-3 py-2 border rounded font-semibold text-xs sm:text-sm">
                  {formatNumberWithCommas(calculateTotalCombinedGallons())}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
                <label className="font-semibold sm:w-32 sm:mr-4 text-gray-700 text-xs sm:text-sm">
                  Total Paid:
                </label>
                <div className="bg-white px-3 py-2 border rounded font-semibold text-xs sm:text-sm">
                  ${formatNumberWithCommas(calculateTotalTax())}
                </div>
              </div>
            </div>

            <div className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-600 italic">
              "We have collected and paid the above Tribal Taxes for our Date/Fuel Fuel Sales"
            </div>

            <div className="mt-3 sm:mt-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
                <label className="font-semibold sm:w-32 sm:mr-4 text-gray-700 text-xs sm:text-sm">
                  Check Date:
                </label>
                <input
                  type="date"
                  name="checkDate"
                  value={formData.checkDate || ''}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, checkDate: e.target.value }));
                  }}
                  className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white w-32 text-xs sm:text-sm"
                />
              </div>
            </div>
          </div>
        )}

        {/* Tax Rate Information */}
        <div className="border border-gray-300 p-3 sm:p-4 mb-4 sm:mb-6 bg-yellow-50">
          <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">
            The Excise tax rate is $0.05 per gallon on all motor fuels.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
            <div>
              <p className="font-semibold">REPORTS ARE DUE:</p>
              <p>10 Calendar Days after the end of each calendar month.</p>
            </div>
            <div>
              <p className="font-semibold">PAYMENTS ARE DUE:</p>
              <p>14 Calendar Days after the end of each calendar month.</p>
            </div>
          </div>
        </div>

        {/* Submission Information */}
        <div className="border border-gray-300 p-3 sm:p-4 mb-4 sm:mb-6 bg-blue-50">
          <p className="text-xs sm:text-sm text-gray-700 mb-2">
            This report is to be submitted to: taxcommission@pbpaneton.org or mailed to:
          </p>
          <p className="text-xs sm:text-sm font-semibold">
            Prairie Band Potawatomi Tax Commission, 16281 Q Road, Mayetta, KS 66509. 785.966.3999
          </p>
          <p className="text-xs sm:text-sm text-blue-600 mt-2">
            Credit Card or ACH Payments can be made via the PBPN Bill Pay Portal at:{' '}
            <a href="https://pay.imsmerchantportal.com/motorfueldistributorretailerlicense" className="underline">
            https://pay.imsmerchantportal.com/motorfueldistributorretailerlicense
            </a>
          </p>
        </div>

        {/* Additional Information */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
            <label className="font-semibold sm:w-64 sm:mr-4 text-gray-700 text-xs sm:text-sm">
              Total number of invoices for the month:
            </label>
            <input
              type="number"
              min="0"
              name="numberOfInvoices"
              value={formData.numberOfInvoices}
              onChange={handleInputChange}
              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white w-32 text-xs sm:text-sm"
              placeholder="0"
            />
          </div>
          <div className="flex items-center">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="noSales"
                checked={formData.noSales}
                onChange={handleInputChange}
                className="mr-2 w-7 h-7"
              />
              <span className="text-xs sm:text-sm font-semibold text-gray-700">
                I certify my business did not sell or make any Motor Fuel deliveries within the PBPN jurisdiction during this filing month.
              </span>
            </label>
          </div>
        </div>

        {/* Invoices Upload Section */}
        <div className="border border-gray-300 p-4 sm:p-6 mb-6 sm:mb-8 rounded-lg">
          <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-800 flex items-center">
            <span className="rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 text-white bg-blue-500">📎</span>
            Upload Invoices <span className="text-red-500 ml-1">(Required)</span>
          </h3>

          <div className="mb-4">

            <div
              className={`border-2 border-dashed rounded-lg sm:rounded-xl p-6 sm:p-10 text-center cursor-pointer transition-all duration-300 ${selectedFiles.length > 0
                ? 'border-emerald-400 bg-emerald-50 shadow-lg scale-[1.02]'
                : 'border-gray-300 hover:shadow-md'
                }`}
              style={{
                borderColor: selectedFiles.length > 0 ? '#34d399' : '#d1d5db',
                backgroundColor: selectedFiles.length > 0 ? '#ecfdf5' : undefined
              }}
              onMouseEnter={(e) => {
                if (selectedFiles.length === 0) {
                  e.target.style.borderColor = '#840028';
                  e.target.style.backgroundColor = '#fdf2f8';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedFiles.length === 0) {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
              onDrop={handleInvoiceDrop}
              onDragOver={handleDragOver}
              onClick={() => invoiceFileInputRef.current?.click()}
            >
              <input
                ref={invoiceFileInputRef}
                type="file"
                onChange={handleInvoiceFileChange}
                className="hidden"
                multiple
              />

              <div className="space-y-4 sm:space-y-6">
                <div className="mx-auto h-16 sm:h-20 w-16 sm:w-20 text-gray-400">
                  {selectedFiles.length > 0 ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-16 sm:w-20 h-16 sm:h-20 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-16 sm:w-20 h-16 sm:h-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  )}
                </div>

                {selectedFiles.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-lg sm:text-xl font-semibold text-emerald-700 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 sm:h-6 w-5 sm:w-6 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {selectedFiles.length} invoice{selectedFiles.length !== 1 ? 's' : ''} selected
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500">
                      Click or drag to add more invoices
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-lg sm:text-xl text-gray-700">
                      <span className="font-semibold" style={{ color: '#840028' }}>
                        Click to upload invoices
                      </span>
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500">
                      All file types accepted • Multiple files allowed • Max file size: 50MB each • Please select file type (Invoice or Bill of Lading) for each file
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* File Preview Grid */}
            {selectedFiles.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">
                  Selected Invoices ({selectedFiles.length})
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {selectedFiles.map((fileObj) => (
                    <div key={fileObj.id} className="relative group">
                      <div className="aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
                        <FilePreview fileObj={fileObj} />
                        <button
                          onClick={() => handleRemoveInvoiceFile(fileObj.id)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          File Type: <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={fileObj.fileType || 'Invoice'}
                          onChange={(e) => handleFileTypeChange(fileObj.id, e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                          required
                        >
                          <option value="Invoice">Invoice</option>
                          <option value="Bill of Lading">Bill of Lading</option>
                        </select>
                      </div>
                      <p className="text-xs text-gray-600 mt-1 truncate" title={fileObj.file.name}>
                        {fileObj.file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(fileObj.file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  ))}
                </div>

                {/* File Actions */}
                <div className="mt-4 flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <button
                    onClick={handleRemoveAllInvoiceFiles}
                    className="flex items-center px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Remove All Invoices
                  </button>
                  <p className="text-sm text-gray-600">
                    Total: {selectedFiles.length} invoice{selectedFiles.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            )}

            {/* Upload Status */}
            {uploadStatus && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-600 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-yellow-700 text-sm">{uploadStatus}</span>
                </div>
              </div>
            )}
          </div>

          <p className="text-xs sm:text-sm text-gray-600 italic">
            Note: Uploading invoices is required. Please select the file type (Invoice or Bill of Lading) for each uploaded file.
          </p>
        </div>

        {/* Signature Section */}
        <div className="border border-gray-300 p-4 sm:p-6 mt-6 sm:mt-8 rounded-lg">
          <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 text-center text-gray-800">Signature</h3>

          <div className="mb-3 sm:mb-4">
            <label className="font-bold block mb-2 text-gray-700 text-sm sm:text-base">
              Sign below to certify the accuracy of this report:
            </label>
            <div
              id="signature-container"
              className="border-2 border-gray-300 rounded-lg p-3 sm:p-4 bg-white"
            >
              {canvasSize.width > 0 && (
                <SignatureCanvas
                  ref={sigCanvas}
                  penColor="#0000FF"
                  backgroundColor="rgb(255, 255, 255)"
                  canvasProps={{
                    width: canvasSize.width,
                    height: canvasSize.height,
                    className: 'signature-canvas w-full border border-gray-400 rounded bg-white cursor-crosshair'
                  }}
                  onBegin={handleSignatureBegin}
                  onEnd={handleSignatureEnd}
                  clearOnResize={false}
                />
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-3 sm:mb-4">
            <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
              {isSigned ? (
                <span className="text-green-600 font-bold">✓ Signature provided</span>
              ) : (
                <span className="text-red-600">Please sign above</span>
              )}
            </div>

            <button
              type="button"
              onClick={clearSignature}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition duration-200 text-xs sm:text-sm"
            >
              Clear Signature
            </button>
          </div>

          {/* Signature Information - Updated with required fields */}
          <div className="border-t pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  Printed Name: <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={printedName}
                  onChange={(e) => setPrintedName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                  placeholder="Enter printed name"
                  required
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  Title: <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                  placeholder="Enter title"
                  required
                />
              </div>
            </div>

            <div className="mt-3 sm:mt-4">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                Date Signed:
              </label>
              <input
                type="text"
                value={currentDate}
                readOnly
                className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 font-medium text-gray-700 cursor-not-allowed text-xs sm:text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                This date is automatically set to today's date and cannot be modified
              </p>
            </div>
          </div>
        </div>

        {/* Certification */}
        <div className="border-t-2 border-b-2 border-gray-500 py-4 sm:py-6 my-6 sm:my-8 text-gray-700 text-xs sm:text-sm">
          <div className="flex items-center justify-center gap-2 sm:gap-3">
            <input
              type="checkbox"
              checked={perjuryCertified}
              onChange={(e) => setPerjuryCertified(e.target.checked)}
              className="w-7 h-7"
              required
            />
            <p className="italic text-center">
              I declare under penalty of perjury under the PBPN law and order codes that the foregoing is true and correct.
            </p>
          </div>
        </div>

        {/* Tax Commission Approval */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0 mb-4 sm:mb-6">
          <label className="font-semibold sm:w-48 sm:mr-4 text-gray-700 text-xs">Tax Commission Approved</label>
        
        </div>
      </div>

      {/* Footer Actions */}
      <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t">
        <div className="flex justify-center">
          <button
            onClick={handleSubmit}
            disabled={!perjuryCertified || !isSigned || submitting || !printedName.trim() || !title.trim() || selectedFiles.length === 0}
            className="px-6 sm:px-8 py-3 bg-[#840028] text-white font-medium rounded-md shadow-sm hover:bg-[#6a0020] focus:outline-none focus:ring-2 focus:ring-[#840028] focus:ring-offset-2 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed w-full sm:w-auto text-sm sm:text-base"
          >
            {submitting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Submitting...
              </span>
            ) : (
              perjuryCertified && isSigned && printedName.trim() && title.trim() && selectedFiles.length > 0 ? 'Submit Report' : 'Please Complete All Required Fields'
            )}
          </button>
        </div>
      </div>

      {/* Custom CSS for signature canvas */}
      <style jsx>{`
        .signature-canvas {
          cursor: crosshair !important;
          touch-action: none;
        }
        .signature-canvas:active {
          cursor: grabbing !important;
        }
        #signature-container {
          min-height: 150px;
        }
        
        @media (min-width: 640px) {
          #signature-container {
            min-height: 200px;
          }
        }
      `}</style>
    </div>
  );
};

export default MotorFuelDistributorReport;